import { App, type AppInfo } from '@capacitor/app'
import type { DeviceId, DeviceInfo } from '@capacitor/device'
import { Device } from '@capacitor/device'
import { jwtDecode } from 'jwt-decode'
import type { $Fetch, FetchContext } from 'ofetch'
import { ofetch } from 'ofetch'

import { useAuthStore } from '~/store'

import type {
  AuthenticationResponse,
  Cart,
  CartCoupon,
  CartCustomerDetails,
  CartDeliveryDetails,
  CartItem,
  CartResponse,
  ChangePasswordRequest,
  CheckoutRequest,
  CheckoutResponse,
  Coupon,
  EightDonateResponse,
  LoginRequest,
  LookupRequest,
  MenuItemExpanded,
  MenuResponse,
  Order,
  OrderAction,
  PastOrder,
  PaymentMethodsResponse,
  PromotionsResponse,
  RefreshRequest,
  RegisterRequest,
  Restaurant,
  TakeOrDonateRequest,
  UpdateUserRequest,
  UserResponse,
  LoginResponse,
  MadBundayResponse,
  CartGiftCard,
  CheckGiftCardsRequest,
  GiftCardCheckResponse,
  NavigationResponse,
  SendStudentVerificationEmailRequest,
  StudentEmailVerificationDto,
  StudentVerifyRequest,
  ConfirmAccountRequest,
  UpdateEmailAddressRequest,
  UpdateMobileNumberRequest,
  CartRecommendationPlacement,
  ReferralRequest,
  ReferralDto,
  CommsPreferencesResponse,
  CommsPreferencesRequest,
  SignUpExperienceResponse,
  AppConfigResponse,
  BurgerPassPaymentIntentDto,
  BurgerPassStatusDto
} from '~/types/api/data-contracts'
import type { GrilldEventsResponse, GrilldEventsRequest } from '~/types/api/events-data-contracts'
import type { Coords } from '~/types/location'
import type { OrderStatus } from '~/types/orderHistory'
import { OnlineOrderType } from '~/types/restaurant'
import type { CreateCartRequest } from '~/types/order'
import * as Sentry from '@sentry/capacitor'

const apiFetch = createFetch()

const CIRCUIT_BREAKER_FAILURE_THRESHOLD = 2
const CIRCUIT_BREAKER_BASE_COOLDOWN_MS = 15000
const CIRCUIT_BREAKER_MAX_COOLDOWN_MS = 120000
const CIRCUIT_BREAKER_LOG_THROTTLE_MS = 10000
const REFRESH_FAILURE_COOLDOWN_MS = 15000

let breakerOpenUntilMs = 0
let consecutiveTransportFailures = 0
let lastBreakerLogMs = 0
let refreshInFlight: Promise<AuthenticationResponse> | null = null
let refreshBlockedUntilMs = 0

let deviceInfo: DeviceInfo | null = null

async function getDeviceInfo(): Promise<DeviceInfo> {
  if (deviceInfo === null) {
    deviceInfo = await Device.getInfo()
  }
  return deviceInfo
}

let deviceId: DeviceId | null = null

async function getDeviceId(): Promise<DeviceId> {
  if (deviceId === null) {
    deviceId = await Device.getId()
  }
  return deviceId
}

let appInfo: AppInfo | null = null

async function getAppInfo(): Promise<AppInfo> {
  if (appInfo === null) {
    appInfo = await App.getInfo()
  }
  return appInfo
}

function isTransportError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false
  }

  const candidate = error as {
    response?: unknown
    name?: string
    message?: string
  }

  if (candidate.response) {
    return false
  }

  const name = candidate.name?.toLowerCase() ?? ''
  const message = candidate.message?.toLowerCase() ?? ''

  return (
    name.includes('abort') ||
    name.includes('timeout') ||
    message.includes('failed to fetch') ||
    message.includes('networkerror') ||
    message.includes('load failed') ||
    message.includes('the internet connection appears to be offline')
  )
}

function isApiCircuitOpen(): boolean {
  return Date.now() < breakerOpenUntilMs
}

function openCircuitOnTransportFailure(error?: unknown): void {
  // A concurrent polling batch can contain several requests (and GET retries).
  // Once one failure has opened the circuit, do not let those in-flight failures
  // repeatedly extend the cooldown.
  if (isApiCircuitOpen()) {
    return
  }

  consecutiveTransportFailures += 1

  if (consecutiveTransportFailures < CIRCUIT_BREAKER_FAILURE_THRESHOLD) {
    return
  }

  const exponent = Math.max(0, consecutiveTransportFailures - CIRCUIT_BREAKER_FAILURE_THRESHOLD)
  const cooldownMs = Math.min(CIRCUIT_BREAKER_BASE_COOLDOWN_MS * 2 ** exponent, CIRCUIT_BREAKER_MAX_COOLDOWN_MS)

  breakerOpenUntilMs = Date.now() + cooldownMs

  if (Date.now() - lastBreakerLogMs > CIRCUIT_BREAKER_LOG_THROTTLE_MS) {
    lastBreakerLogMs = Date.now()
    if (import.meta.env.VITE_DEPLOY_ENV === 'local') {
      console.warn('API circuit breaker opened after transport errors', {
        consecutiveTransportFailures,
        cooldownMs,
        reason: (error as { message?: string } | undefined)?.message
      })
    }
  }
}

function closeCircuitOnSuccess(): void {
  consecutiveTransportFailures = 0
  breakerOpenUntilMs = 0
}

export function getApiCircuitBreakerState() {
  return {
    isOpen: isApiCircuitOpen(),
    openUntil: breakerOpenUntilMs || undefined,
    consecutiveTransportFailures
  }
}

function createFetch(): $Fetch {
  // Note the default retry is 1, and will only retry GET requests to avoid side effects. If retry count is changed from the default, it will retry all requests
  return ofetch.create({
    // Retry on additional status code 401 to allow token refresh
    retryStatusCodes: [401, 408, 409, 425, 429, 500, 502, 503, 504],
    retryDelay: 500,
    async onRequest(context) {
      const authStore = useAuthStore()
      // If is authenticated, refresh token (if necessary) and add the token to the request
      const headers = context.options.headers

      if (authStore.authSession) {
        // Preemptively refresh the token if it is about to expire
        if (checkRefreshToken(context)) {
          await shouldRefreshToken()
        }
        headers.set('Authorization', `Bearer ${authStore.authSession.idToken}`)
      }
      const deviceId = await getDeviceId()
      if (isPlatform('capacitor')) {
        const deviceInfo = await getDeviceInfo()
        const appInfo = await getAppInfo()
        // Add platform and version information to the headers
        headers.set('X-Grilld-App-Platform', deviceInfo.platform)
        headers.set('X-Grilld-App-Platform-Version', deviceInfo.osVersion)
        headers.set('X-Grilld-App-Version', appInfo.version)
        headers.set('X-Grilld-Device-Id', deviceId.identifier)
      } else {
        headers.set('X-Grilld-App-Platform', 'web')
        if (import.meta.env.VITE_APP_VERSION) {
          headers.set('X-Grilld-App-Version', import.meta.env.VITE_APP_VERSION)
        }
        headers.set('X-Grilld-Device-Id', deviceId.identifier)
      }
      context.options.headers = headers
    },
    async onRequestError(context) {
      if (isTransportError(context.error)) {
        openCircuitOnTransportFailure(context.error)
      }
    },
    async onResponse() {
      closeCircuitOnSuccess()
    },
    async onResponseError(context) {
      // Refresh token if a 401 response is returned. GET requests will be retried automatically
      const authStore = useAuthStore()

      // If we get a 400 error from the auth/refresh endpoint, sign the user out

      if (context.response?.status === 400 && context.request.toString().endsWith('/auth/refresh')) {
        console.warn('Received 400 from /auth/refresh, signing out')
        Sentry.captureMessage('Received 400 from /auth/refresh, signing out', 'warning')
        authStore.signOut()
        return
      }

      if (authStore.isAuthenticated && context.response?.status === 401) {
        const authResponse = await refreshTokenAndUpdateSession(
          authStore.getAuthSession!.refreshToken!,
          authStore.updateUserAndSession
        )
        // Replace the old token with the new one
        const headers = context.options.headers
        headers.set('Authorization', `Bearer ${authResponse.session?.idToken}`)
        context.options.headers = headers

        // Do not retry the request here, rely on the ofetch retry mechanism
        // Note, ofetch will not retry on POST, PUT, PATCH and DELETE
      }
    }
  })

  /**
   * @description Checks if the token needs to be refreshed
   * @param context {FetchContext} - The fetch context from ofetch
   * @returns {boolean} - True if the token needs to be refreshed
   */
  function checkRefreshToken(context: FetchContext): boolean {
    if (context.options.method === undefined) {
      return false
    }
    // return false if the request is in the suppress list
    const anonymous = ['/auth/refresh', '/auth/login', '/auth/register', '/auth/lookup', '/feedback']
    const request = context.request
    if (typeof request === 'string') {
      return !anonymous.includes(request)
    } else if (typeof request === 'object') {
      return !anonymous.includes(request.url)
    }
    return true
  }
}

/**
 * @description Preemptively refresh the token if it is about to expire
 * @returns {Promise<void>}
 */
export async function shouldRefreshToken(): Promise<void> {
  const authStore = useAuthStore()
  const token = authStore.authSession?.idToken

  if (token) {
    const decoded = jwtDecode(token)
    // refresh token if it is about to expire
    const timeToRefresh = Math.floor(Date.now() / 1000) + 60 * 5 // 5 minutes before expiry

    if (decoded?.exp && decoded.exp < timeToRefresh) {
      await refreshTokenAndUpdateSession(authStore.getAuthSession!.refreshToken!, authStore.updateUserAndSession)
    }
  }
}

export async function refreshTokenAndUpdateSession(
  refreshToken: string,
  updateUserAndSession: (authSession: LoginResponse, authUser: UserResponse) => void
) {
  if (Date.now() < refreshBlockedUntilMs && !refreshInFlight) {
    const retryAfterMs = Math.max(0, refreshBlockedUntilMs - Date.now())
    throw new Error(`Refresh token request is cooling down after network failures. Retry after ${retryAfterMs}ms.`)
  }

  let isSingleFlightOwner = false
  if (!refreshInFlight) {
    isSingleFlightOwner = true
    refreshInFlight = refreshAuthTokens({ refreshToken })
  }

  try {
    const authResponse = await refreshInFlight

    if (!authResponse.session || !authResponse.user) {
      throw new Error('Could not refresh auth token')
    }

    refreshBlockedUntilMs = 0
    updateUserAndSession(authResponse.session, authResponse.user)
    return authResponse
  } catch (error) {
    if (isTransportError(error)) {
      refreshBlockedUntilMs = Date.now() + REFRESH_FAILURE_COOLDOWN_MS
    }
    throw error
  } finally {
    if (isSingleFlightOwner) {
      refreshInFlight = null
    }
  }
}

interface DefaultOptions {
  baseURL: string
  headers: Record<string, string>
}

/**
 * @description Returns base api + version with optional endpoint
 * @param endpoint {string}
 * @returns {string}
 */
export function getApi(endpoint?: string): string {
  const config = useRuntimeConfig()

  if (endpoint && !endpoint.startsWith('/')) {
    endpoint = `/${endpoint}`
  }
  return `${config.public.baseApiUrl}/${config.public.apiVersion}${endpoint}`
}

export function getApiTest(): Promise<string> {
  const config = useRuntimeConfig()

  const params = {
    baseURL: `${config.public.baseApiUrl}/`,
    headers: {}
  }
  return apiFetch(`/`, params)
}

function defaultOptions(): DefaultOptions {
  const config = useRuntimeConfig()

  return {
    baseURL: `${config.public.baseApiUrl}/${config.public.apiVersion}`,
    headers: {}
  }
}

function eventsApiOptions(): DefaultOptions {
  const config = useRuntimeConfig()

  return {
    baseURL: config.public.baseEventsApiUrl ? `${config.public.baseEventsApiUrl}/v1` : '',
    headers: {}
  }
}

export function fetchAppConfigStatus(): Promise<AppConfigResponse> {
  const options = {
    method: 'GET'
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)

  return apiFetch('/config/app', params)
}

export function fetchMenuItemById({
  restaurantId,
  orderType,
  productId
}: {
  restaurantId: number
  orderType: number
  productId: number
}): Promise<MenuItemExpanded> {
  const options = {
    method: 'GET'
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/restaurants/${restaurantId}/menu/${productId}?orderType=${orderType}`, params)
}

/**
 * @description Client-side fetch cart by cartId
 * @param cartId {string}
 */
export function getCart(cartId: string): Promise<CartResponse> {
  const params = defaultOptions()
  return apiFetch(`/carts/${cartId}`, params)
}

/**
 * @description Gets customers order via orderId
 * @param orderId {string}
 */
export function getOrder(orderId: string): Promise<Order> {
  const params = defaultOptions()
  return apiFetch(`/Orders/${orderId}`, params)
}

/**
 * @description Creates a cart
 * @param restaurantId {number}
 * @param orderType {number}
 * @param orderTime {string | undefined}
 * @param deliveryDetails {CartDeliveryDetails}
 * @param vehicleDetails {CartVehicleDetails}
 * @param tableDetails {CartTableDetails}
 * @param cateringDetails {CartCateringDetails}
 * @param pushNotificationToken {string | null | undefined}
 */
export function createCart({
  restaurantId,
  orderType,
  orderTime,
  deliveryDetails,
  vehicleDetails,
  tableDetails,
  cateringDetails,
  pushNotificationToken
}: CreateCartRequest): Promise<CartResponse> {
  let body: CreateCartRequest = {
    restaurantId,
    orderType,
    orderTime,
    pushNotificationToken
  }

  switch (orderType) {
    case OnlineOrderType.Catering:
      if (cateringDetails && cateringDetails.date && cateringDetails.time) {
        body = {
          ...body,
          cateringDetails
        }
      }
      break
    case OnlineOrderType.Delivery:
      if (
        deliveryDetails &&
        deliveryDetails.addressLine1 &&
        deliveryDetails.suburb &&
        deliveryDetails.state &&
        deliveryDetails.postcode
      ) {
        body = {
          ...body,
          deliveryDetails
        }
      }
      break
    case OnlineOrderType['Order at Table']:
      if (tableDetails && tableDetails.tableNumber) {
        body = {
          ...body,
          tableDetails
        }
      }
      break
    case OnlineOrderType['Park & Collect']:
      if (
        vehicleDetails &&
        vehicleDetails.registrationNumber &&
        vehicleDetails.colour &&
        vehicleDetails.make &&
        vehicleDetails.model
      ) {
        body = {
          ...body,
          vehicleDetails
        }
      }
      break
    case OnlineOrderType['Drive Thru']:
      if (vehicleDetails && vehicleDetails.registrationNumber) {
        body = {
          ...body,
          vehicleDetails
        }
      }
      break
  }

  const options = {
    method: 'POST',
    body
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch('/carts', params)
}

/**
 * @description Client-side request to delete a cart
 * @param cartId {number}
 * @returns {Promise<Cart>}
 */
export function deleteCart(cartId: string): Promise<Cart> {
  const options = {
    method: 'DELETE'
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/carts/${cartId}`, params)
}

/**
 * @description Client-side request to update cart items; before using, first consider if orderStore.updateOrder() is
 * more appropriate which will also sync the store with the updated cart
 * @param cartId {string}
 * @param cart {Cart[]}
 * @returns {Promise<CartResponse>}
 */
export function updateCartItems(
  cartId: string,
  cart: CartItem[],
  validateAvailability: boolean
): Promise<CartResponse> {
  const queryParams = new URLSearchParams()
  if (validateAvailability) {
    queryParams.set('validateAvailability', 'true')
  }
  const options = {
    method: 'PUT',
    body: cart,
    query: Object.fromEntries(queryParams.entries())
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/carts/${cartId}/items`, params)
}

/**
 * @description Client-side request to update coupons applied to a cart.
 * @param cartId {string} - Cart to update
 * @param cartCoupon {CartCoupon} - Coupon to apply to cart
 * @return {Promise<CartResponse>}
 */
export function updateCartCoupons(cartId: string, cartCoupon?: CartCoupon): Promise<CartResponse> {
  const options = {
    method: 'PUT',
    body: cartCoupon
  }

  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)

  return apiFetch(`/carts/${cartId}/coupons`, params)
}

/**
 * @description Client-side request to remove coupons applied to a cart.
 * @param cartId {string} - Cart to remove coupons from
 * @return {Promise<CartResponse>}
 */
export function removeCartCoupons(cartId: string): Promise<CartResponse> {
  const options = {
    method: 'DELETE'
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/carts/${cartId}/coupons`, params)
}

/**
 * @description Client-side request to update a cart
 * @param cartId {string}
 * @param cart {Cart[]}
 * @returns {Promise<CartResponse>}
 */
export function updateCart(cartId: string, cart: Cart): Promise<CartResponse> {
  const options = {
    method: 'PUT',
    body: cart
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/carts/${cartId}`, params)
}

/**
 * @description Fetch list of supported payment methods
 * @param cartId {string}
 * @returns {Promise<PaymentMethodsResponse>}
 */
export function preparePaymentMethods(cartId: string): Promise<PaymentMethodsResponse> {
  const options = {
    method: 'POST'
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/carts/${cartId}/prepare`, params)
}

/**
 * @description Checkout with payment method
 * @param cartId {string}
 * @param checkoutRequestBody {CheckoutRequest}
 * @returns {Promise<Order>}
 */
export function checkout(cartId: string, checkoutRequestBody: CheckoutRequest): Promise<CheckoutResponse> {
  const options = {
    method: 'POST',
    body: checkoutRequestBody
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/carts/${cartId}/checkout`, params)
}

export function checkoutWithAdditionalDetails(
  cartId: string,
  checkoutRequestBody: CheckoutRequest
): Promise<CheckoutResponse> {
  const options = {
    method: 'POST',
    body: checkoutRequestBody
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/carts/${cartId}/checkout/payment-details`, params)
}

/**
 * @description Updates cart with customer details
 * @param cartId {string}
 * @param cartCustomerDetails {CartCustomerDetails}
 * @returns {Promise<CartCustomerDetails>}
 */
export function updateCartCustomer(
  cartId: string,
  cartCustomerDetails: CartCustomerDetails
): Promise<CartCustomerDetails> {
  const options = {
    method: 'PUT',
    body: cartCustomerDetails
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/carts/${cartId}/customer`, params)
}

/**
 * @description Client-side request to update gift card applied to a cart.
 * @param cartId {string} - Cart to update
 * @param giftCard {CartGiftCard} - gift card to apply to the cart
 * @return {Promise<CartResponse>}
 */
export function updateCartGiftCard(cartId: string, giftCard?: CartGiftCard): Promise<CartResponse> {
  const options = {
    method: 'PUT',
    body: giftCard
  }

  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)

  return apiFetch(`/carts/${cartId}/gift-card`, params)
}

/**
 * @description Client-side request to remove gift card applied to a cart.
 * @param cartId {string} - Cart to remove gift card from
 * @return {Promise<CartResponse>}
 */
export function removeCartGiftCard(cartId: string): Promise<CartResponse> {
  const options = {
    method: 'DELETE'
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/carts/${cartId}/gift-card`, params)
}

/**
 * @description Checks the balances and expiry dates of multiple gift cards.
 * @param request {CheckGiftCardsRequest} - Gift cards to check
 * @returns {Promise<GiftCardCheckResponse[]>}
 */
export function checkGiftCards(request: CheckGiftCardsRequest): Promise<GiftCardCheckResponse[]> {
  const options = {
    method: 'POST',
    body: request
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)

  return apiFetch(`/gift-cards/check`, params)
}

/**
 * @description Sends an action to the restaurant
 * @param orderId {string}
 * @param action {OrderAction}
 * @returns {Promise<CartCustomerDetails>}
 */
export function postOrderAction(orderId: string, action: OrderAction): Promise<CartCustomerDetails> {
  const options = {
    method: 'POST',
    body: action
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/orders/${orderId}/action`, params)
}

/**
 * @description Vote for a local matter at a restaurant
 * @param orderId {string} - Order to vote on
 * @param groupAllocationId {string} - Group allocation to vote for
 * @returns {Promise<void>} - 200 if successful, 404 if order or group allocation not found
 */
export function voteOnLocalMatter(orderId: string, groupAllocationId: string): Promise<void> {
  const options = {
    method: 'POST',
    body: { groupAllocationId }
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/orders/${orderId}/vote`, params)
}

/**
 * @description Fetch single restaurant by id
 * @param restaurantId
 * @returns {Promise<Restaurant>}
 */
export function fetchRestaurantById(restaurantId: number): Promise<Restaurant> {
  const options = {
    method: 'GET'
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/restaurants/${restaurantId}`, params)
}

/**
 * @description Fetch list of coupons
 * @returns {Promise<Coupon[]>}
 */
export function fetchCoupons(): Promise<Coupon[]> {
  const options = {
    method: 'GET'
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)

  return apiFetch(`/coupons`, params)
}

/**
 * @description Fetch a list of coupons available for a cart
 * @param cartId {string}
 * @returns {Promise<Coupon[]>}
 */
export function fetchAvailableCoupons(cartId: string): Promise<Coupon[]> {
  const options = {
    method: 'GET'
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/carts/${cartId}/coupons`, params)
}

/**
 * @description Fetch a list of add ons recommendations for a cart
 * @param cartId {string}
 * @param placement {CartRecommendationPlacement}
 * @returns {Promise<CartItem[]>}
 */
export function fetchCartAddOnsRecommendations(
  cartId: string,
  placement: CartRecommendationPlacement
): Promise<CartItem[]> {
  const options = {
    method: 'GET'
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/carts/${cartId}/recommendations?placement=${placement}`, params)
}

/**
 * @description Lookups email to see if it already exists
 * @param lookupRequest
 * @returns {Promise<boolean | Error>} - True if email exists, false if it does not, Error if something went wrong
 */
export async function lookupEmail(lookupRequest: LookupRequest): Promise<boolean | Error> {
  const options = {
    method: 'POST',
    body: lookupRequest
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)

  try {
    await apiFetch(`/auth/lookup`, params)
    // 200 means email exists in the system
    return true
  } catch (error: any) {
    if (error.response.status === 404) {
      // 404 means email does not exist in the system
      return false
    }

    return error
  }
}

/**
 * @description Registers account
 * @param registerRequest
 * @returns {Promise<AuthenticationResponse>}
 */
export function register(registerRequest: RegisterRequest): Promise<AuthenticationResponse> {
  const options = {
    method: 'POST',
    body: registerRequest
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/auth/register`, params)
}

/**
 * @description Refresh user auth tokens
 * @param refreshRequest
 * @returns {Promise<AuthenticationResponse>}
 */
export function refreshAuthTokens(refreshRequest: RefreshRequest): Promise<AuthenticationResponse> {
  const options = {
    method: 'POST',
    body: refreshRequest
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/auth/refresh`, params)
}

/**
 * @description Logs in user
 * @param logInRequest
 * @returns {Promise<AuthenticationResponse>}
 */
export function signIn(logInRequest: LoginRequest): Promise<AuthenticationResponse> {
  const options = {
    method: 'POST',
    body: logInRequest
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/auth/login`, params)
}

/**
 * @description Updates user details
 * @param userDetails {UpdateUserRequest}
 * @returns {Promise<UserResponse>}
 */
export function updateUserDetails(userDetails: UpdateUserRequest): Promise<UserResponse> {
  const options = {
    method: 'PUT',
    body: userDetails
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/auth/update`, params)
}

/**
 * @description Changes user password
 * @param changePasswordRequest {ChangePasswordRequest}
 * @returns {Promise<AuthenticationResponse>}
 */
export function updateUserPassword(changePasswordRequest: ChangePasswordRequest): Promise<AuthenticationResponse> {
  const options = {
    method: 'POST',
    body: changePasswordRequest
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/auth/changePassword`, params)
}

/**
 * @description Deletes user account
 * @returns {Promise<void>}
 */
export function deleteUser(): Promise<void> {
  const options = {
    method: 'DELETE'
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/auth/delete`, params)
}

/**
 * @description Validates user address to see if user is close enough for delivery from restaurant
 * @param createCartRequest
 * @return {Promise<any>}
 */
export function validateDelivery(createCartRequest: CreateCartRequest): Promise<CartDeliveryDetails> {
  const options = {
    method: 'POST',
    body: createCartRequest
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/carts/validate-delivery`, params)
}

/**
 * @description Gets a Relish user's loyalty stamp (Eight & Donate)
 * @param userId {string} - User id
 * @return {Promise<EightDonateResponse>}
 */
export function getEightAndDonate(userId: string): Promise<EightDonateResponse> {
  const options = {
    method: 'GET'
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/loyalty/${userId}`, params)
}

/**
 * @description Submits users take or donate preference
 * @param userId
 * @param takeOrDonateAction
 * @return {Promise<EightDonateResponse>}
 */
export function submitTakeOrDonate(userId: string, takeOrDonateAction: TakeOrDonateRequest): Promise<any> {
  const options = {
    method: 'POST',
    body: takeOrDonateAction
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/loyalty/${userId}/takeOrDonate`, params)
}

/**
 * @description Fetches nearby restaurants given a lat/lng and optional limit
 * @param coords {Coords}
 * @param limit {number}
 * @param tag {string | null}
 * @returns {Promise<Restaurant[]>}
 */
export function fetchNearbyRestaurants(coords: Coords, limit = 10, tag: string | null = null): Promise<Restaurant[]> {
  const options = {
    method: 'GET'
  }

  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  const tagQuery = tag ? `&tag=${tag}` : ''
  return apiFetch(`restaurants/nearby?lat=${coords.latitude}&lng=${coords.longitude}&limit=${limit}${tagQuery}`, params)
}

/**
 * @description Fetches product recommendations for a Relish user
 * @param userId {string}
 * @param restaurantId {number}
 * @param orderType {OnlineOrderType}
 * @return {Promise<MenuItem[]>}
 */
export function getProductRecommendations({
  userId,
  restaurantId,
  orderType
}: {
  userId: string
  restaurantId: number
  orderType: OnlineOrderType
}): Promise<CartItem[]> {
  const options = {
    method: 'GET',
    query: {
      restaurantId: restaurantId.toString(),
      orderType: orderType.toString()
    }
  }

  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)

  return apiFetch(`/loyalty/${userId}/menuRecommendations`, params)
}

export function resetPassword(email: string): Promise<any> {
  const options = {
    method: 'POST',
    body: {
      emailAddress: email
    }
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`auth/reset`, params)
}

export function getMenuForRestaurant(restaurantId: number, orderType: OnlineOrderType): Promise<MenuResponse> {
  const options = {
    method: 'GET'
  }

  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/restaurants/${restaurantId}/menu?orderType=${orderType}`, params)
}

export function getOrderHistory(userId: string): Promise<Array<PastOrder>> {
  const options = {
    method: 'GET'
  }

  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/loyalty/${userId}/orders`, params)
}

export function fetchOrderStatus(orderId: string, stage?: number): Promise<OrderStatus> {
  const options = {
    method: 'GET'
  }

  const stageQuery = stage ? `&stage=${stage}` : ''

  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/orders/${orderId}/status${stageQuery}`, params)
}

export function getPromotions(
  restaurantId: number | undefined,
  orderType: OnlineOrderType
): Promise<PromotionsResponse> {
  let queryParams = new URLSearchParams()
  if (restaurantId) {
    queryParams.set('restaurantId', restaurantId.toString())
  }
  queryParams.set('orderType', orderType.toString())
  queryParams = mobilePlatformQueryParam(queryParams)
  const options = {
    method: 'GET',
    query: Object.fromEntries(queryParams.entries())
  }

  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)

  return apiFetch(`/promotions/`, params)
}

export function getNavigationMenu(): Promise<NavigationResponse> {
  const options = {
    method: 'GET'
  }

  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  // Assume API takes query parameter for platform and returns all user menu items for that platform
  // and also determines if user is authenticated
  return apiFetch('/config/navigation', params)
}

export function fetchCateringParameters(restaurantId: number): Promise<{
  dateFrom: string
  dateTo: string
  excludedDates: string[]
  timesOfDay: string[]
  orderingMessage: string
}> {
  const options = {
    method: 'GET'
  }

  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/carts/catering-parameters?restaurantId=${restaurantId}`, params)
}

export function getMadBundayEntries(userId: string): Promise<MadBundayResponse> {
  const options = {
    method: 'GET'
  }

  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/loyalty/${userId}/madBundayEntries`, params)
}

export function fetchUserLoyaltyTags(userId: string): Promise<UserResponse['tags']> {
  const options = {
    method: 'GET'
  }

  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/loyalty/${userId}/tags`, params)
}

export function updateLoyaltyTags(userId: string, tags: UserResponse['tags']): Promise<void> {
  const options = {
    method: 'PUT',
    body: tags
  }

  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/loyalty/${userId}/tags`, params)
}

export function sendFeedback(message: string): Promise<any> {
  const options = {
    method: 'POST',
    body: {
      message
    }
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/feedback`, params)
}

export function sendStudentsVerificationEmail(
  userId: string,
  email: SendStudentVerificationEmailRequest
): Promise<StudentEmailVerificationDto> {
  const options = {
    method: 'POST',
    body: email
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/loyalty/${userId}/student/sendVerification`, params)
}

export function verifyStudentEmailCode(
  userId: string,
  verificationId: string,
  code: StudentVerifyRequest
): Promise<StudentEmailVerificationDto> {
  const options = {
    method: 'POST',
    body: code
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/loyalty/${userId}/student/${verificationId}/verify`, params)
}

export function getReferrals(userId: string): Promise<ReferralDto[]> {
  const options = {
    method: 'GET'
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/loyalty/${userId}/referrals`, params)
}

export function addReferral(userId: string, payload: ReferralRequest): Promise<any> {
  const options = {
    method: 'POST',
    body: payload
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/loyalty/${userId}/referrals`, params)
}

export function updateEmail(data: UpdateEmailAddressRequest): Promise<void> {
  const options = {
    method: 'POST',
    body: data
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/auth/update-email`, params)
}

export function confirmEmail(data: ConfirmAccountRequest): Promise<AuthenticationResponse> {
  const options = {
    method: 'POST',
    body: data
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/auth/confirm-email`, params)
}

export function updateMobile(data: UpdateMobileNumberRequest): Promise<void> {
  const options = {
    method: 'POST',
    body: data
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/auth/update-mobile`, params)
}

export function confirmMobile(data: ConfirmAccountRequest): Promise<AuthenticationResponse> {
  const options = {
    method: 'POST',
    body: data
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/auth/confirm-mobile`, params)
}

export function fetchCommsPreferences(userId: string): Promise<CommsPreferencesResponse> {
  const options = {
    method: 'GET'
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/loyalty/${userId}/comms`, params)
}

export function updateCommsPreferences(
  userId: string,
  commsPreferences: CommsPreferencesRequest
): Promise<CommsPreferencesResponse> {
  const options = {
    method: 'PUT',
    body: commsPreferences
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/loyalty/${userId}/comms`, params)
}

export function fetchSignUpExperience(
  experienceCode?: string,
  referralCode?: string
): Promise<SignUpExperienceResponse> {
  const options = {
    method: 'GET',
    query: { experienceCode, referralCode }
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/config/sign-up-experience`, params)
}

export function fetchBurgerPassStatus(): Promise<BurgerPassStatusDto> {
  const options = {
    method: 'GET'
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/burger-pass`, params)
}

export function getBurgerPassPaymentIntent(): Promise<BurgerPassPaymentIntentDto> {
  const options = {
    method: 'POST',
    body: {}
  }
  const params: DefaultOptions = Object.assign({}, defaultOptions(), options)
  return apiFetch(`/burger-pass`, params)
}

export function getApiErrorMessage(error: any): string | undefined {
  if (error.data?.message && typeof error.data.message === 'string') {
    return error.data.message
  }

  if (error.data?.error && typeof error.data.error === 'string') {
    return error.data.error
  }

  if (error.data?.title && typeof error.data.title === 'string') {
    return error.data.title
  }

  if (typeof error.data === 'string') {
    return error.data
  }

  return undefined
}

export function getApiErrorTitle(error: any): string | undefined {
  if (error.data?.title && typeof error.data.title === 'string') {
    return error.data.title
  }

  return undefined
}

export function getApiErrorCode(error: any): string | undefined {
  if (error.data?.code && typeof error.data.code === 'string') {
    return error.data.code
  }
  return undefined
}

export function getApiErrorDetails(error: any): any | undefined {
  if (error.data?.details) {
    return error.data.details
  }
  return undefined
}

/**
 * @description Send analytic event
 * @param body {CheckoutRequest}
 * @returns {Promise<GrilldEventsResponse>}
 */
export function track(body: GrilldEventsRequest): Promise<GrilldEventsResponse> {
  const options = {
    method: 'POST',
    body
  }
  const eventsOptions = eventsApiOptions()
  if (!eventsOptions.baseURL) {
    return Promise.resolve({} as GrilldEventsResponse)
  }
  const params: DefaultOptions = Object.assign({}, eventsOptions, options)
  return apiFetch(`/analytics/event`, params)
}
